/**
 * Timezone helpers for Tasks — never hardcode abbreviations.
 */

export function resolveTaskTimezone({ userTimezone, agencyTimezone, platformTimezone, fallback = 'UTC' } = {}) {
  return userTimezone || agencyTimezone || platformTimezone || fallback;
}

const NAIVE_ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Parse a task instant as UTC. Task timestamps are stored/served in UTC, but a
 * timezone-less ISO string (e.g. "2026-07-17T04:53:00") is parsed by
 * `new Date()` as *local* time — which shifts reminder firing by the viewer's
 * UTC offset. Treat naive datetimes as UTC so scheduling and display agree.
 */
export function parseTaskInstant(value) {
  if (value == null || value === '') return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const str = String(value);
  return new Date(NAIVE_ISO_DATETIME.test(str) ? `${str}Z` : str);
}

export function getTimezoneAbbreviation(date, timeZone) {
  try {
    const d = date instanceof Date ? date : new Date(date || Date.now());
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find((p) => p.type === 'timeZoneName')?.value || timeZone || 'UTC';
  } catch {
    return timeZone || 'UTC';
  }
}

export function formatTaskDueAt(dueAt, timeZone) {
  if (!dueAt) return '—';
  try {
    const d = parseTaskInstant(dueAt);
    if (Number.isNaN(d.getTime())) return '—';
    const formatted = d.toLocaleString(undefined, {
      timeZone: timeZone || 'UTC',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const abbr = getTimezoneAbbreviation(d, timeZone);
    return `${formatted} (${abbr})`;
  } catch {
    return String(dueAt);
  }
}

export function dueDateColumnLabel(timeZone) {
  const abbr = getTimezoneAbbreviation(new Date(), timeZone);
  return `Due Date (${abbr})`;
}

export function plainTextFromHtml(html = '') {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
}

export function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
